import { HowTo } from '../types';

/**
 * Posts How Tos.
 *
 * Writing a post and publishing one are deliberately separate: publishing is a
 * safeguarding decision made by someone with the Reporting role, in a different
 * part of the app, and folding it into "create a post" would imply it is the
 * last step of writing one.
 */
export const postsHowTos: HowTo[] = [
  {
    key: 'posts.create-post',
    version: 1,
    section: 'Posts',
    title: 'Create a post for a child',
    summary: 'Write up a moment, add photos, and save it as a draft.',
    youNeed: ['A child to write about', 'Permission to edit posts'],
    keywords: [
      'post', 'posts', 'create', 'new', 'add', 'write', 'writing', 'observation',
      'note', 'photo', 'photos', 'picture', 'pictures', 'image', 'upload', 'draft',
      'record', 'moment', 'typo', 'typos', 'spelling', 'grammar', 'correct',
      'check', 'proofread',
    ],
    routes: ['/posts'],
    audience: 'staff',
    requiredRole: 'Editor',
    steps: [
      {
        text: 'Open **Add / Edit Posts** from the menu.',
        target: 'nav.posts',
        route: '/posts',
        navigateOnly: true,
      },
      {
        // Posts can't be shown without a child, so arriving with none selected
        // hands the whole content area to the picker; the compact rail is what
        // you get once a child is chosen.
        text: "Choose the child. The picker fills the page until one is chosen; after that, use the rail on the left.",
        // The picker while it fills the page; the rail once a child is chosen.
        target: ['child-picker', 'child-rail'],
      },
      {
        text: "Click **New Post**. The post is dated today.",
        target: 'posts.new-post',
      },
      {
        text: 'Write the post in the panel on the right.',
        target: 'posts.editor',
      },
      {
        text: 'Optional: **✨ Fix typos** corrects spelling and grammar without changing what you said. It shows the change first — **Accept suggestion** or **Reject**.',
        target: 'posts.fix-typos',
      },
      {
        text: 'Add photos by dragging them onto the **Photos** box, or click it to choose files.',
        target: 'posts.photos',
      },
      {
        text: 'To hide a face, click **Blur faces** on the photo. The blurred version is the one shown and published everywhere.',
        target: 'posts.blur',
        seeAlso: { guideKey: 'posts.blur-face', label: 'Show me how to blur a face' },
      },
      {
        text: "Click **Save**. Until you do, the footer says **Unsaved changes**. The post saves as a **draft** — it reaches a report only once someone with the Reporting role publishes it.",
        target: 'posts.save',
      },
    ],
    related: ['posts.blur-face', 'posts.publish-post'],
  },
  {
    key: 'posts.publish-post',
    version: 1,
    section: 'Posts',
    title: 'Publish a post so it appears in the report',
    summary:
      "Review a draft — its wording and its photos — and publish it. Only published posts go into a report, and publishing locks the post against further editing.",
    youNeed: [
      'The Reporting role',
      'A draft post for the child in the period',
    ],
    keywords: [
      'publish', 'publishing', 'approve', 'approval', 'review', 'draft',
      'unpublish', 'release', 'share', 'lock', 'locked', 'sign off',
      'post', 'posts', 'report', 'period', 'week', 'missing', 'find',
    ],
    routes: ['/reports'],
    audience: 'staff',
    requiredRole: 'Reporting',
    steps: [
      {
        // One step, two places: the menu item from elsewhere, the tab strip once
        // you are on Reports — and "Take me there" is still offered from the
        // wrong tab, since the ring can only frame the strip, not the tab meant.
        text: 'Open **Reports** from the menu and go to the **Build Report** tab. Publishing happens here, beside the report the posts feed into — **Report History** is for reports already generated.',
        target: ['reports.tabs', 'nav.reports'],
        route: '/reports?tab=build',
        navigateOnly: true,
      },
      {
        text: 'Choose the child. The picker fills the page until one is chosen; after that, the rail on the left.',
        // The picker while it fills the page; the rail once a child is chosen.
        target: ['child-picker', 'child-rail'],
      },
      {
        text: 'Move to the period holding the draft — the list only shows the period on screen, so last week\'s post is not in this week. The **Template** beside it sets how long that period is.',
        target: 'period-nav',
      },
      {
        text: 'Click **Draft** to leave the published posts out of the list. The number beside it is how many are still waiting.',
        target: 'reports.post-filter',
      },
      {
        text: 'Click the post you want to publish. The review window opens.',
        target: 'reports.posts-list',
      },
      {
        text: "Read the post as it will read in the report. Use the AI wording, edit it, or keep the original — what you publish is what the report shows. **✨ Fix typos** is here too.",
      },
      {
        text: "If the post has photos, check every one, exclude any that should not go in, then tick **I've reviewed all photos in this post**. Publishing is blocked until you do.",
        target: 'publish.photo-check',
        seeAlso: { guideKey: 'posts.exclude-photo', label: 'Show me how to exclude a photo' },
      },
      {
        text: 'Click **Publish to Report** when you are satisfied. This is the decision that puts the post into the report and locks it, so it is yours to make, not the guide\'s.',
        target: 'publish.button',
      },
      {
        text: 'The post is now in the report and locked. To change it: **Unpublish**, edit, publish again.',
      },
    ],
    related: ['posts.create-post', 'reports.generate-pdf'],
  },
  {
    key: 'posts.unpublish-post',
    version: 1,
    section: 'Posts',
    title: 'Change a post that has already been published',
    summary:
      'A published post is locked. Unpublish it, make the change, then publish it again.',
    youNeed: ['The Reporting role'],
    keywords: [
      'unpublish', 'locked', 'lock', 'edit', 'change', 'amend', 'correct',
      'mistake', 'wrong', 'published', 'remove from report', 'retract',
      'post', 'posts', 'typo', 'photo',
    ],
    routes: ['/reports', '/posts'],
    audience: 'staff',
    requiredRole: 'Reporting',
    steps: [
      {
        text: 'Open **Reports** from the menu and go to the **Build Report** tab.',
        target: ['reports.tabs', 'nav.reports'],
        route: '/reports?tab=build',
        navigateOnly: true,
      },
      {
        text: 'Choose the child, then move to the period holding the post.',
        target: 'period-nav',
      },
      {
        text: 'Click **Published** to list only the posts already in the report.',
        target: 'reports.post-filter',
      },
      {
        text: 'Click the post. It opens showing the published record, which is read-only — that is the lock.',
        target: 'reports.posts-list',
      },
      {
        text: 'Click **Unpublish** in the ribbon at the top and confirm. The post goes back to draft and leaves the report until you publish it again.',
        target: 'publish.unpublish',
      },
      {
        text: 'Make the change in **Add / Edit Posts** — text, photos and blurring are all edited there, not here.',
        target: 'nav.posts',
        route: '/posts',
      },
      {
        text: 'Publish it again when you are happy with it, the same way you published it the first time.',
      },
    ],
    related: ['posts.publish-post', 'posts.create-post'],
  },
  {
    key: 'posts.locked-post',
    version: 1,
    section: 'Posts',
    // No requiredRole: this is the one anybody can read, and the only thing
    // help can offer someone who cannot act. It explains and hands off, which
    // is why its "steps" are statements rather than instructions.
    title: "Why can't I edit this post?",
    summary: 'Published posts are locked. Here is what that means and how to get one changed.',
    keywords: [
      'locked', 'lock', "can't edit", 'cannot edit', 'read only', 'greyed out',
      'disabled', 'published', 'change', 'edit', 'why', 'post', 'posts',
    ],
    routes: ['/posts'],
    audience: 'staff',
    steps: [
      {
        text: 'The post is **published**, so it is part of a report. It is locked to keep what a report says from changing after the fact.',
      },
      {
        text: 'A locked post shows the **Locked** badge, and its text and photos cannot be edited — by anyone, including whoever wrote it. Clicking the post flashes that badge rather than letting you type.',
      },
      {
        text: 'To get it changed, ask a colleague with the **Reporting** role. They can unpublish it, which returns it to draft so it can be edited, and publish it again afterwards.',
      },
      {
        text: 'Tell them which post and what needs to change. Anything still in **draft** you can edit yourself, as normal.',
      },
    ],
    related: ['posts.create-post'],
  },
  {
    key: 'posts.blur-face',
    version: 1,
    section: 'Posts',
    title: 'Blur a face in a photo',
    summary:
      'Hide a face before a photo goes anywhere. The blurred version becomes the one the app shows and publishes.',
    youNeed: ['A post you can edit, with a photo on it'],
    keywords: [
      'blur', 'blurred', 'blurring', 'face', 'faces', 'hide', 'anonymise',
      'anonymize', 'pixelate', 'obscure', 'privacy', 'consent', 'permission',
      'safeguarding', 'photo', 'photos', 'picture', 'image', 'redact',
      'other children', 'remove blur', 'undo blur', 'unblur', 'full size',
      'check', 'compare', 'original',
    ],
    routes: ['/posts'],
    audience: 'staff',
    requiredRole: 'Editor',
    steps: [
      {
        text: 'Open the post in **Add / Edit Posts** and start editing it — click its text or its photos, or use the **Edit** button.',
        target: 'nav.posts',
        route: '/posts',
      },
      {
        text: 'On the photo, click **Blur faces**. The editor opens with the photo full size.',
        target: 'posts.blur',
      },
      {
        text: 'Faces the detector found are already marked. **Blur all** applies every one of them in a single click.',
        target: 'redactor.blur-all',
      },
      {
        text: 'Missed one? Click **Draw** and drag an oval over it. Ovals can be moved, resized and removed again, so a misplaced one costs nothing.',
        target: 'redactor.draw',
      },
      {
        text: 'Choose **Blur** or **Pixelate** and set the **Strength**. Zoom in to check the result before accepting it — this is the version everyone will see.',
      },
      {
        text: 'Click **Apply & use photo**, then save the post. Nothing is uploaded until you save.',
        target: 'redactor.apply',
      },
      {
        text: 'From now on the blurred version is what the app shows and what a report publishes. The original is kept, but it is not what anyone sees.',
      },
      {
        text: 'Check it: click the photo to see it full size — before saving as well as after. The bar underneath says which version you are looking at.',
        target: 'posts.view-full-size',
      },
      {
        text: '**Re-blur faces** re-opens the editor on the original with no boxes — the earlier blur is not kept, so it means starting again. It is also how to see the photo unblurred. **Remove blur** asks first, then reloads showing the original.',
        target: 'posts.edit-blur',
      },
    ],
    related: ['posts.create-post', 'posts.exclude-photo'],
  },
  {
    key: 'posts.exclude-photo',
    version: 1,
    section: 'Posts',
    title: 'Leave a photo out of the report',
    summary:
      'Keep a photo on the post but stop it reaching the report — a different decision from blurring, which changes the photo itself.',
    youNeed: ['The Reporting role', 'A post you are reviewing'],
    keywords: [
      'exclude', 'excluded', 'leave out', 'omit', 'remove from report', 'hide',
      'not in report', 'photo', 'photos', 'picture', 'image', 'report',
      'publish', 'privacy', 'safeguarding', 'consent',
    ],
    routes: ['/reports'],
    audience: 'staff',
    requiredRole: 'Reporting',
    steps: [
      {
        text: 'Open **Reports** and open the post you are reviewing.',
        target: 'nav.reports',
        route: '/reports?tab=build',
      },
      {
        text: 'Click the photo in the tray to see it full size.',
      },
      {
        text: 'Click **Exclude photo** and give a reason. The photo stays on the post, where staff can still see it; the report simply leaves it out.',
        target: 'publish.exclude-photo',
      },
      {
        text: 'Excluded photos show dimmed in the review tray, so the decision is visible to whoever looks next.',
      },
      {
        text: '**Exclude** when a photo should not go into the report at all; **blur** when it can go with a face hidden. Blurring changes the photo everywhere.',
      },
    ],
    related: ['posts.blur-face', 'posts.publish-post'],
  },
  {
    key: 'posts.restore-deleted',
    version: 1,
    section: 'Posts',
    title: 'Restore a post that was deleted',
    summary:
      'Bring back a deleted post — including its photos — within the few days before it goes for good.',
    youNeed: [
      'Permission to edit posts',
      'A post deleted in the last week — after that it cannot be recovered',
    ],
    keywords: [
      'restore', 'restored', 'undelete', 'undo', 'deleted', 'delete', 'removed',
      'recover', 'recovery', 'back', 'mistake', 'accident', 'gone', 'lost',
      'bin', 'trash', 'recently deleted', 'post', 'posts', 'photo', 'photos',
    ],
    routes: ['/posts'],
    audience: 'staff',
    requiredRole: 'Editor',
    steps: [
      {
        text: 'Open **Add / Edit Posts** from the menu and pick the child whose post was deleted.',
        target: ['child-picker', 'nav.posts'],
        route: '/posts',
      },
      {
        text: 'Click **Recently deleted** in the toolbar. The number beside it is how many can still be recovered — no number, nothing to recover.',
        target: 'posts.recently-deleted',
      },
      {
        text: 'Each deleted post shows the days it has left. After that it is gone permanently; the last two days are marked urgent.',
      },
      {
        text: 'Click the post to open it. It reads as it did, so you can be sure it is the right one before bringing it back.',
        target: 'posts.list',
      },
      {
        text: 'Click **Restore Post**. It returns to the normal list with its photos, exactly as it was — a deleted post keeps everything until the window closes.',
        target: 'posts.restore',
      },
      {
        text: 'Once the countdown runs out, the post cannot be brought back by anyone.',
      },
    ],
    related: ['posts.create-post'],
  },
];



